import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { PlusCircle } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import {
  ENDPOINTS,
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SUPER_ADMIN
} from '../../helpers/constants';
import { userHasAnyRole } from '../../helpers/roleHelpers';
import SearchableSelect from '../common/SearchableSelect';
import FloatingButton from '../common/FloatingButton';
import NotFound from '../NotFound';
import CompleteModal from './CompleteModal';
import CustomerDrawer from './CustomerDrawer';
import { boardRequest, canPinBook, choiceValues, firstName, greeting, pinOptions, postToday } from './helpers';
import MyMoneyTab from './MyMoneyTab';
import NoteModal from './NoteModal';
import PipelineTab from './PipelineTab';
import QuickAddModal from './QuickAddModal';
import TodayTab from './TodayTab';
import './TopProducerView.scss';

const tabs = [
  ['today', 'Today'],
  ['pipeline', 'Pipeline'],
  ['money', 'My Money']
];

const boardQuery = (storeId, managerId, userId) => {
  const params = new URLSearchParams();
  if (storeId && storeId !== 'all') params.set('salesLocation', storeId);
  if (managerId && managerId !== 'all') params.set('salesManager', managerId);
  if (userId && userId !== 'all') params.set('contactOwner', userId);
  const query = params.toString();
  return query ? `?${query}` : '';
};

function FilterSelect({ id, label, value, options, onChange }) {
  return (
    <div className="today-filter">
      <label htmlFor={id}>{label}</label>
      <SearchableSelect
        inputId={id}
        size="sm"
        options={options}
        value={options.some(option => option.value === value) ? value : 'all'}
        onChange={next => onChange(next || 'all')}
        isClearable={false}
        aria-label={label}
        menuPortalTarget={null}
        menuPosition="absolute"
      />
    </div>
  );
}

function TopProducerView() {
  const history = useHistory();
  const allowed = userHasAnyRole(
    ROLE_SALES,
    ROLE_SALES_MANAGER,
    ROLE_SALES_AND_INVENTORY_MANAGER,
    ROLE_ADMIN,
    ROLE_SUPER_ADMIN
  );
  const [view, setView] = useState('today');
  const [counts, setCounts] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [appChoices, setAppChoices] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [noteLead, setNoteLead] = useState(null);
  const [completeLead, setCompleteLead] = useState(null);
  const [drawerLead, setDrawerLead] = useState(null);
  const [storeId, setStoreId] = useState('all');
  const [managerId, setManagerId] = useState('all');
  const [userId, setUserId] = useState('all');
  const showPin = canPinBook();
  const adminFilters = userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN);
  const pin = useMemo(() => (showPin ? pinOptions(appChoices) : { stores: [], users: [], managers: [] }), [
    showPin,
    appChoices
  ]);

  const scopeQuery = boardQuery(storeId, managerId, userId);
  const reload = () => setReloadKey(key => key + 1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancel = false;
    setCounts(null);
    boardRequest(ENDPOINTS.CONTACTS_TOP_PRODUCER_COUNTS, scopeQuery, {
      fresh: reloadKey ? '1' : '0'
    }).then(response => {
      if (!cancel && response) setCounts(response);
    });
    return () => {
      cancel = true;
    };
  }, [allowed, scopeQuery, reloadKey]);

  useEffect(() => {
    if (!allowed) return;
    makeApiRequests({ endpoint: ENDPOINTS.APP_CHOICES_LIST }).then(({ response }) => {
      if (Array.isArray(response)) {
        setAppChoices(response);
        setLeadSources(choiceValues(response, 'Lead Source'));
        setStatuses(choiceValues(response, 'Contact Status'));
      }
    });
  }, [allowed]);

  const atStore = (person, id) => id === 'all' || (person.salesLocations || []).map(String).includes(String(id));

  const managersAtStore = (pin.managers || []).filter(manager => atStore(manager, storeId));

  const repsForFilters =
    adminFilters && managerId !== 'all'
      ? (() => {
          const manager = (pin.managers || []).find(item => String(item._id) === String(managerId));
          return manager ? [{ _id: manager._id, name: manager.name }, ...(manager.reps || [])] : [];
        })()
      : pin.users.filter(person => atStore(person, storeId));

  const withAll = people => [
    { label: 'All', value: 'all' },
    ...people.map(person => ({ label: person.location || person.name, value: String(person._id) }))
  ];

  const selectStore = id => {
    setDrawerLead(null);
    setStoreId(id || 'all');
    setManagerId('all');
    setUserId('all');
  };

  const selectManager = id => {
    setDrawerLead(null);
    setManagerId(id || 'all');
    setUserId('all');
  };

  if (!allowed) {
    return <NotFound text="Top Producer View is available to sales, manager, and admin roles." />;
  }

  const finishTask = async lead => {
    const saved = await postToday(ENDPOINTS.CONTACTS_TODAY_COMPLETE(lead._id), { taskId: lead.taskId });
    if (!saved) return;
    toast.success(`Task marked complete for ${lead.buyer}`);
    reload();
  };

  const onComplete = lead => {
    if (lead?.taskId) {
      finishTask(lead);
      return;
    }
    setCompleteLead(lead);
  };

  const openDrawer = (lead, showNextAction) => setDrawerLead({ ...lead, showNextAction });

  return (
    <div className="today-workspace">
      <div className="today-toolbar">
        <div className="container-fluid today-pad py-3">
          <div className="d-md-flex align-items-md-end justify-content-md-between">
            <div>
              <div className="text-uppercase text-muted small font-weight-bold">{counts?.todayLabel || 'Today'}</div>
              <h1 className="h4 mb-0 font-weight-bold">
                {greeting()}, {firstName()}
              </h1>
            </div>
            {showPin && (
              <div className="today-filters ml-md-auto">
                {adminFilters && (
                  <>
                    <FilterSelect
                      id="today-sales-location"
                      label="Sales Location"
                      value={storeId}
                      options={withAll(pin.stores)}
                      onChange={selectStore}
                    />
                    <FilterSelect
                      id="today-sales-manager"
                      label="Sales Manager"
                      value={managerId}
                      options={withAll(managersAtStore)}
                      onChange={selectManager}
                    />
                  </>
                )}
                <FilterSelect
                  id="today-sales-rep"
                  label="Sales Rep"
                  value={userId}
                  options={withAll(repsForFilters)}
                  onChange={id => {
                    setDrawerLead(null);
                    setUserId(id || 'all');
                  }}
                />
              </div>
            )}
          </div>
          <div className="today-tabs d-flex mt-3">
            {tabs.map(([key, label], index) => (
              <Button
                key={key}
                variant={view === key ? 'primary' : 'light'}
                className={`flex-fill ${index < tabs.length - 1 ? 'mr-2' : ''}`}
                onClick={() => setView(key)}
              >
                {label}
                {counts ? ` (${counts[key] || 0})` : ''}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="container-fluid today-pad pt-4">
        {view === 'today' && (
          <TodayTab
            scopeQuery={scopeQuery}
            reloadKey={reloadKey}
            onOpen={lead => openDrawer(lead, true)}
            onReload={reload}
            onNote={setNoteLead}
            onComplete={onComplete}
          />
        )}
        {view === 'pipeline' && (
          <PipelineTab scopeQuery={scopeQuery} reloadKey={reloadKey} onOpen={lead => openDrawer(lead, true)} />
        )}
        {view === 'money' && (
          <MyMoneyTab scopeQuery={scopeQuery} reloadKey={reloadKey} onOpen={deal => openDrawer(deal, false)} />
        )}
      </div>

      {view !== 'money' && (
        <FloatingButton variant="success" text="Add New Contact" Icon={PlusCircle} onClick={() => setAddOpen(true)} />
      )}
      {addOpen && (
        <QuickAddModal
          leadSources={leadSources}
          statuses={statuses}
          onClose={() => setAddOpen(false)}
          onCreated={reload}
        />
      )}
      {noteLead && (
        <NoteModal
          lead={noteLead}
          onClose={() => setNoteLead(null)}
          onSaved={() => {
            setNoteLead(null);
            toast.success(`Note added for ${noteLead.buyer}`);
          }}
        />
      )}
      {completeLead && (
        <CompleteModal
          lead={completeLead}
          onClose={() => setCompleteLead(null)}
          onSaved={message => {
            setCompleteLead(null);
            toast.success(message);
            reload();
          }}
        />
      )}
      {drawerLead && (
        <CustomerDrawer
          lead={drawerLead}
          appChoices={appChoices}
          onClose={() => setDrawerLead(null)}
          onNote={() => setNoteLead(drawerLead)}
          onComplete={() => onComplete(drawerLead)}
          onReschedule={() => setCompleteLead(drawerLead)}
          onOpenRecord={() => history.push(`/crm/${drawerLead._id}`)}
          onTasksSaved={reload}
          onStatusChange={status => {
            setDrawerLead(current => (current ? { ...current, status } : current));
            reload();
          }}
          showNextAction={drawerLead.showNextAction !== false}
        />
      )}
    </div>
  );
}

export default TopProducerView;
